"use client";

import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle } from "lucide-react";
import { authApi, toApiError } from "@/lib/api";
import { getAuthMode } from "@/lib/auth/auth-mode";
import { getSamlLoginUrl } from "@/lib/auth/saml-config";
import {
  clearLoginErrorFromLocation,
  formatLoginErrorMessage,
  readLoginErrorFromLocation,
} from "@/lib/auth/login-error";
import {
  clearSignInPending,
  getSignInLoadingLabel,
  markSignInStarted,
} from "@/lib/auth/sign-in-pending";
import { useSignInUiState } from "@/hooks/use-sign-in-ui";
import { toast } from "sonner";

function MicrosoftIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 23 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M11 0H0V11H11V0Z" fill="#F25022" />
      <path d="M23 0H12V11H23V0Z" fill="#7FBA00" />
      <path d="M11 12H0V23H11V12Z" fill="#00A1F1" />
      <path d="M23 12H12V23H23V12Z" fill="#FFB900" />
    </svg>
  );
}

function subscribeToLocation() {
  return () => {};
}

function getLoginErrorSnapshot(): string | null {
  const raw = readLoginErrorFromLocation();
  return raw ? formatLoginErrorMessage(raw) : null;
}

export default function LoginForm() {
  const { isLoading, phase } = useSignInUiState();
  const authMode = getAuthMode();
  const samlLoginUrl = getSamlLoginUrl();
  const samlConfigured = Boolean(samlLoginUrl);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [urlErrorDismissed, setUrlErrorDismissed] = useState(false);
  const [stickyUrlError, setStickyUrlError] = useState<string | null>(null);
  const urlError = useSyncExternalStore(
    subscribeToLocation,
    getLoginErrorSnapshot,
    () => null,
  );

  if (urlError !== null && urlError !== stickyUrlError) {
    setStickyUrlError(urlError);
  }

  const authError =
    submitError ?? (urlErrorDismissed ? null : stickyUrlError);

  useEffect(() => {
    if (!urlError) return;
    clearSignInPending();
    clearLoginErrorFromLocation();
    toast.error(urlError);
  }, [urlError]);

  const handleSignIn = async () => {
    setSubmitError(null);
    setUrlErrorDismissed(true);
    markSignInStarted("redirecting");

    try {
      if (authMode === "saml") {
        if (!samlConfigured) {
          throw new Error(
            "SAML SSO is not configured. Set NEXT_PUBLIC_API_BASE_URL or NEXT_PUBLIC_AUTH_LOGIN_URL.",
          );
        }
        await authApi.login();
        return;
      }

      if (authMode === "backend") {
        await authApi.login();
        return;
      }

      await authApi.login();
      clearSignInPending();
      window.location.href = "/dashboard/";
    } catch (err) {
      clearSignInPending();
      const message = toApiError(err, "sign-in").message;
      setSubmitError(message);
      toast.error(message);
    }
  };

  const showSso = authMode === "saml" || authMode === "backend";

  const loadingLabel = isLoading
    ? getSignInLoadingLabel(phase, authMode)
    : "Sign in with Microsoft";

  return (
    <div className="space-y-4">
      {authError && (
        <div
          role="alert"
          className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{authError}</p>
        </div>
      )}

      {authMode === "saml" && !samlConfigured && (
        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {process.env.NODE_ENV === "development" ? (
              <>
                Sign-in is not configured. Set{" "}
                <code className="rounded bg-amber-100 px-1">
                  NEXT_PUBLIC_API_BASE_URL
                </code>{" "}
                (or{" "}
                <code className="rounded bg-amber-100 px-1">
                  NEXT_PUBLIC_AUTH_LOGIN_URL
                </code>
                ) in{" "}
                <code className="rounded bg-amber-100 px-1">.env.local</code>,
                then restart the dev server.
              </>
            ) : (
              "Sign-in is temporarily unavailable. Please contact your administrator."
            )}
          </p>
        </div>
      )}

      {showSso && (
        <Button
          type="button"
          onClick={handleSignIn}
          disabled={isLoading}
          aria-busy={isLoading}
          className="flex h-13 w-full items-center justify-center gap-3 rounded-md border border-slate-200 bg-white text-base font-semibold text-slate-900 shadow-sm transition-all duration-200 hover:bg-slate-50 hover:shadow-md active:scale-[0.98] disabled:pointer-events-none disabled:opacity-80"
          variant="outline"
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          ) : (
            <MicrosoftIcon />
          )}
          {loadingLabel}
        </Button>
      )}

      {authMode === "mock" && (
        <>
          <Button
            type="button"
            onClick={handleSignIn}
            disabled={isLoading}
            aria-busy={isLoading}
            className="flex h-13 w-full items-center justify-center gap-3 rounded-md border border-slate-200 bg-white text-base font-semibold text-slate-900 shadow-sm disabled:pointer-events-none disabled:opacity-80"
            variant="outline"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            ) : null}
            {isLoading ? "Signing in..." : "Sign in"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full text-sm text-slate-500"
            disabled={isLoading}
            onClick={handleSignIn}
          >
            Continue with demo account (local only)
          </Button>
        </>
      )}
    </div>
  );
}
